/**
 * 流水页的**中文名唯一源** + 行文案派生（纯逻辑 · 零 DOM）
 *
 * ## 🔴 为什么单独一个文件
 * 与 `account-labels.ts` / `policy-status.ts` 同一理由：同一个中文名若在
 * 筛选轨、行、抽屉三处各写一遍，改名时必漏一处 —— 而**漏改不会报错**（只是两处不一致）。
 * ⇒ 全部收敛到本文件；枚举取值集用**闭合 `Record`**（新增枚举值 = 编译错误，不是静默缺一档）。
 *
 * ## 🔴 本文件里的判据一律"转发"，不自己判
 * 形态（转账 / 多腿）、是否标注"不计收支"、来源 —— 都取**服务端派生好的字段**
 * （`parent.kind` / `parent.hasExcludedLegs` / `row.source`）。
 * 端侧**不做形态判定**（`AC-C1.7` 的判定在共享层 `deriveParentRow`，此处只选文案）。
 */

import {
  ParentRowKind,
  TxnDirectionFilter,
  TxnSourceFilter,
  TxnStatusFilter,
  formatMoneyText,
  netAfterRefund,
  sumCents,
  toCents,
  type Cents,
  type TransactionLegView,
  type TransactionRowView,
} from '@jiaying/shared';
import { JournalWindow } from './journal-filter.js';

// ═══════════════════════ 各维度的选项（顺序 = 展示顺序） ═══════════════════════

export interface FilterOption<T extends string> {
  id: T;
  label: string;
  /** 补充说明（如"默认"）—— 只展示，不参与任何判定 */
  hint?: string;
}

/** 「默认」标注文案（只有一档会带它 —— 见 `windowOptionsFor`） */
export const DEFAULT_WINDOW_HINT = '默认';

/**
 * 全量时间档（**不带**「默认」标注）—— 顺序 = 展示顺序：
 * C 域 PRD §5.1.4 的三项（近 3 月 / 本月 / 上月 / 今年至今 / 自定义）+ 🆕 N 域批 `59` 的两个滚动档
 * （近 1 月 / 近 6 月，`AC-N2.18` 的 1 / 3 / 6 月需要一一对应的落点）。
 */
const WINDOW_LIST: readonly FilterOption<JournalWindow>[] = [
  { id: JournalWindow.Recent3m, label: '近 3 个月' },
  { id: JournalWindow.Recent1m, label: '近 1 个月' },
  { id: JournalWindow.Recent6m, label: '近 6 个月' },
  { id: JournalWindow.ThisMonth, label: '本月' },
  { id: JournalWindow.LastMonth, label: '上月' },
  { id: JournalWindow.ThisYear, label: '今年至今' },
  { id: JournalWindow.Custom, label: '自定义区间' },
];

/**
 * 时间档选项，**「默认」标注落在 `base` 那一档**。
 *
 * 🔴 为什么不是写死的 `hint: '默认'` 常量：用户把默认窗口设成「近 6 月」后，
 * 把「默认」标在「近 3 月」上是**假信息**（他会以为自己进错档了）。
 * 标注必须跟着**这个用户的基线**走 —— 与 `activeDimensionCount(draft, base)` 同源。
 */
export function windowOptionsFor(base: JournalWindow): readonly FilterOption<JournalWindow>[] {
  return WINDOW_LIST.map((o) => (o.id === base ? { ...o, hint: DEFAULT_WINDOW_HINT } : o));
}

/** 默认基线下（近 3 月）的选项表 —— 供"无偏好上下文"处（如单测）直接使用 */
export const WINDOW_OPTIONS: readonly FilterOption<JournalWindow>[] = windowOptionsFor(
  JournalWindow.Recent3m,
);

/** 维度 7「方向」—— 🔴 单选；取值与契约 `TxnDirectionFilter` 一一对应 */
export const DIRECTION_OPTIONS: readonly FilterOption<TxnDirectionFilter>[] = [
  { id: TxnDirectionFilter.Income, label: '收入' },
  { id: TxnDirectionFilter.Expense, label: '支出' },
  { id: TxnDirectionFilter.Transfer, label: '转账' },
  { id: TxnDirectionFilter.Excluded, label: '不计收支' },
];

/** 维度 8「状态」—— 最后一项 `deleted` 是**回收站开关**（契约里有专门说明） */
export const STATUS_OPTIONS: readonly FilterOption<TxnStatusFilter>[] = [
  { id: TxnStatusFilter.Uncategorized, label: '未分类' },
  { id: TxnStatusFilter.Unpaid, label: '只看未付' },
  { id: TxnStatusFilter.Refund, label: '只看退款' },
  { id: TxnStatusFilter.Deleted, label: '已删除', hint: '回收站' },
];

/** 维度 9「来源」 */
export const SOURCE_OPTIONS: readonly FilterOption<TxnSourceFilter>[] = [
  { id: TxnSourceFilter.Manual, label: '手工' },
  { id: TxnSourceFilter.Import, label: '导入批次' },
  { id: TxnSourceFilter.Schedule, label: '周期生成' },
  { id: TxnSourceFilter.Ai, label: 'AI 快记' },
];

/** 来源的中文名（行内展示用）—— 🔴 **闭合 `Record`**：新增来源取值即编译错误 */
export const SOURCE_LABELS: Record<TxnSourceFilter, string> = {
  [TxnSourceFilter.Manual]: '手工',
  [TxnSourceFilter.Import]: '导入',
  [TxnSourceFilter.Schedule]: '周期',
  [TxnSourceFilter.Ai]: 'AI 快记',
};

// ═══════════════════════ 行文案派生 ═══════════════════════

/** 行的主文案：**分类优先**，无分类才用备注（都没有才显示占位）—— `DR-066` D2 · `AC-B5.3` */
export function rowTitle(row: TransactionRowView): string {
  const names = distinctCategoryNames(row);
  if (names.length > 0) {
    // 🔴 拆成 3 类以上时长名字会把整行挤满 ⇒ 前 2 个 + 总数（例：`餐饮、日用 等3类`）
    return names.length <= MAX_TITLE_CATEGORIES
      ? names.join('、')
      : `${names.slice(0, MAX_TITLE_CATEGORIES).join('、')} 等${String(names.length)}类`;
  }
  const note = noteOf(row);
  return note !== '' ? note : '（未分类）';
}

/**
 * 行的副文案 = `[备注] · 日期 · 账户 · [含 N 笔]`（缺项自动省略）。
 *
 * 🔴 **备注只在有分类时出现**（`DR-066` D2）：无分类时备注已经占了第一行，
 * 副行再写一遍就是同一句话出现两次。
 * 🔴 **分类不再进副行**：它已经在第一行了 —— 修前"分类兜底到主文案"的设计
 * 让副行成了分类的第二个出口，两处同名反而让人以为是两项。
 *
 * 🔴 转账形态**用两端账户名**（`parent.transfer`）：它的"主账户"概念不存在
 * （`DR-004` 下跨币种两侧金额本就不等，净额为 0 无意义）⇒ 显示 `转出 → 转入`。
 */
export function rowSubtitle(row: TransactionRowView): string {
  const parts: string[] = [];
  if (distinctCategoryNames(row).length > 0) {
    const note = noteOf(row);
    if (note !== '') parts.push(note);
  }
  parts.push(row.date);
  const from = row.parent.transfer;
  if (row.parent.kind === ParentRowKind.Transfer && from !== null) {
    parts.push(`${accountNameOf(row, from.fromAccountId)} → ${accountNameOf(row, from.toAccountId)}`);
  } else {
    const accounts = distinct(row.legs.map((l) => l.accountName));
    if (accounts.length > 0) parts.push(accounts.join('、'));
  }
  // 🔴 多笔（拆分 / 工资条）要让人看出"这笔不止一项" —— 文案不出现「腿」（`legs` 是数据模型词）
  if (row.legs.length > 1) parts.push(`含 ${String(row.legs.length)} 笔`);
  return parts.join(' · ');
}

/** 主文案里最多列几个分类名（`DR-066` D2：> 2 个 ⇒ `前2个 等N类`） */
const MAX_TITLE_CATEGORIES = 2;

/** 备注的**归一**形态（空白串 ≡ 没有备注 —— 修前 `'  '` 会被当成有备注） */
function noteOf(row: TransactionRowView): string {
  return (row.note ?? '').trim();
}

/** 本行的分类名（**去重保序**，跳过空名）—— 主 / 副文案共用同一份取数 */
function distinctCategoryNames(row: TransactionRowView): string[] {
  return distinct(
    row.legs
      .map((l) => l.categoryName)
      .filter((n): n is string => n !== null && n.trim() !== ''),
  );
}

/**
 * 🔴 「不计收支」的标注（`AC-C1.7`）—— **只看服务端派生的 `parent.hasExcludedLegs`**。
 *
 * ⚠️ 为什么不用"方向是 transfer"来判：`hasExcludedLegs` 覆盖的是**所有**含不计收支腿的笔
 * （工资条里的社保/公积金转账子项也算），而 `kind === transfer` 只是其中一种形态。
 */
export function excludedBadge(row: TransactionRowView): string | null {
  return row.parent.hasExcludedLegs ? '不计收支' : null;
}

/** 记录人之外的**归属人**文字（抽屉三行用）—— 由服务端 `attribution` 算好，此处只拼 */
export function ownersText(names: readonly string[]): string {
  return names.length === 0 ? '—' : names.join('、');
}

/** 账户名：从**本行的腿**里取（数据已在响应内 ⇒ 不是第二次请求，也不是 join 口径） */
export function accountNameOf(row: TransactionRowView, accountId: string): string {
  const leg = row.legs.find((l) => l.accountId === accountId);
  return leg?.accountName ?? accountId;
}

/**
 * 腿的**展示符号投影**：`in` ⇒ 正 · `out` ⇒ 负。
 *
 * ## 🔴 这是"投影"不是"计算"，两者别混
 * `legs[].amount` 按契约恒为**正数**（`AmountInput` 明文 `.positive()`：退款用 `in` 腿表达，
 * **不许用负数 `out`**），方向由 `legs[].direction` 承担 —— 这是数据模型层的分工。
 * 而展示层需要一个**带符号的钱数**（`Amount` 的着色与 `−` 号都按符号走，`AC-C1.6`）。
 * ⇒ 本函数只做**方向 → 符号**的搬运：**不换算、不折算、不聚合**（跨币种也不参与）。
 *
 * ⚠️ 与 `parent.net` 的区别：`parent.net` 是**主账户净变动**（多腿代数和，口径在
 * `deriveParentRow`）；本函数处理的是**单条腿**（如抽屉里的逐腿明细），不要拿它替代 `parent.net`。
 */
export function legSignedCents(leg: Pick<TransactionLegView, 'direction' | 'amount'>): Cents {
  return (leg.direction === 'in' ? leg.amount : -leg.amount) as Cents;
}

/**
 * 一行的**展示金额**（含是否着色 / 是否带正负号）。
 *
 * 🔴 **只取服务端派生的 `parent`，端侧一个数都不算**：
 * - 转账 ⇒ `parent.transfer.fromAmount`（跨币种两侧本就不等，取"转出"一侧），
 *   且 **不着色**（资金搬运没有"流出/流入"的收支语义）；
 * - 其余 ⇒ `parent.net`（多笔交易的**净变动**，口径在共享层 `deriveParentRow`），着色 + 带符号。
 *
 * 🔴 **流水页与报表下钻抽屉（`AC-K2.9`）共用本函数** —— 一处实现，两处渲染
 * （各写一份是"两套口径"的开端，而这里恰好是金额）。
 */
export function rowAmount(row: TransactionRowView): {
  cents: Cents;
  colored: boolean;
  signed: boolean;
} {
  if (row.parent.kind === ParentRowKind.Transfer && row.parent.transfer !== null) {
    return { cents: row.parent.transfer.fromAmount, colored: false, signed: false };
  }
  /**
   * 🆕 批 `120`（`DR-069` D1）：**被退过的消费，行上显示净额**。
   *
   * 净额 = 本笔净额 + 已退合计（内核 `netAfterRefund()` 是唯一实现 —— 端侧不写 `+`）。
   * 🔴 不改 `parent.net`：可退额度 / 对账 / 导出都在用它，它必须保持"这一笔交易的腿净额"原义。
   * 🔴 **不再减优惠**（`DR-069` D2）：`amount` 本就是实付、`discount` 只作记录。
   */
  const net = (row.parent.net ?? 0) as Cents;
  const refunded = (row.refunds?.amount ?? 0) as Cents;
  return { cents: netAfterRefund(net, refunded), colored: true, signed: true };
}

/** 稳定去重（保序）—— 展示层的去重，不改变任何顺序口径 */
function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/**
 * 🆕 批 `110`：一行的**笔级优惠合计**（整数分 · 原币）。`0` = 这笔没填过优惠。
 *
 * 🔴 只做**求和**，不派生任何东西：
 * - 「原价」= `amount + discount` 的唯一实现在共享层 `lineOriginal()`（`nucleus/discount.ts`），
 *   端侧**不得**在这里再写一次 `+`（铁律 24 防双实现）；
 * - 币种不由本函数决定 —— 调用方用 `row.parent.currency`（一行的腿同币种；
 *   跨币种转账那种多币行，优惠语义本身不存在，见 changelog §5 诚实登记）。
 */
export function rowDiscount(row: TransactionRowView): Cents {
  return sumCents(row.legs.map((l) => l.discount as Cents));
}

// ═══════════════════════ 退款可见性（批 `120` · `DR-069`） ═══════════════════════

/**
 * 这笔消费**是否已被全额退掉**（`DR-069` D4）。
 *
 * 全额退 ⇒ 金额列显示「已全额退款」而不是 `¥0.00` —— 后者会被读成"没花钱"或"数据坏了"，
 * 而全额退完其实是一笔**已结清**的消费。
 */
export function isFullyRefunded(row: TransactionRowView): boolean {
  const net = row.parent.net ?? 0;
  if (net >= 0 || row.refunds === null) return false;
  return row.refunds.amount >= -net;
}

/**
 * 被退过的消费，副行那句：`已退 ¥50.00 · 原 ¥−175.50`（`DR-069` D3）。
 *
 * 🔴 **原额必须同时可见**：用户点"退款"时要判断"当初到底花了多少"，只说"已退"不够。
 * 🔴 金额格式一律走 `formatMoneyText`（符号单源 `currency.ts` · `DR-036`），端侧不自己拼 ¥。
 */
export function refundHint(row: TransactionRowView): string | null {
  const r = row.refunds;
  if (r === null || r.amount <= 0) return null;
  const cur = row.parent.currency;
  const refunded = formatMoneyText(toCents(r.amount), cur);
  const original = formatMoneyText((row.parent.net ?? 0) as Cents, cur);
  const count = r.count > 1 ? `（${String(r.count)} 笔）` : '';
  return `已退 ${refunded}${count} · 原 ${original}`;
}

/**
 * 退款行的副行：`冲减：争鲜寿司（10-20）`（`DR-069` D5）。
 *
 * 修前退款行在列表上与普通收入行**完全同形** —— 只有点开详情抽屉才有一行"冲减自…"
 * （`TransactionDrawer.tsx`），而列表才是用户看账的地方。
 *
 * ⚠️ 父已被物理清理 ⇒ `parentBrief.date` 是空串 ⇒ 只给名字、不显示日期
 * （是否为"已删除"由 `parentDeleted` 另外标注，`AC-C2.15`）。
 */
export function chargeBackHint(row: TransactionRowView): string | null {
  const b = row.parentBrief;
  if (b === null) return null;
  const name = (b.note ?? '').trim();
  const who = name === '' ? '原交易' : name;
  const when = b.date.trim() === '' ? '' : `（${b.date.slice(5)}）`;
  return `冲减：${who}${when}`;
}
